import React from 'react';
import { useTheme } from '@mui/material';
import DSModal from '../../../design-system/DSModal';
import DSBox from '../../../design-system/DSBox';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import { DSTitleTypography } from '../../../design-system/Typography';
import DSValueTypography from '../../../design-system/Typography/DSValueTypography';
import DSCircularProgress from '../../../design-system/DSCircularProgress';

export enum PreviewUnavailableReason {
  NOT_CONNECTED = 'not-connected',
  NEEDS_UPDATE = 'needs-update',
}

interface PreviewUnavailableModalProps {
  open: boolean;
  onClose: () => void;
  reason: PreviewUnavailableReason;
  onUpdate: () => void;
  isUpdating?: boolean;
  /** Overrides for flows that are not previewing a klydo (e.g. sending). */
  title?: string;
  message?: string;
}

const messageByReason: Record<PreviewUnavailableReason, string> = {
  [PreviewUnavailableReason.NOT_CONNECTED]:
    'Connect your KlydoClock to Wi-Fi to continue.',
  [PreviewUnavailableReason.NEEDS_UPDATE]:
    'Update your KlydoClock to preview this Klydo.',
};

const PreviewUnavailableModal = ({
  open,
  onClose,
  reason,
  onUpdate,
  isUpdating = false,
  title = 'Preview Unavailable',
  message,
}: PreviewUnavailableModalProps) => {
  const { spacing, palette } = useTheme();

  return (
    <DSModal open={open} onClose={onClose}>
      <DSBox
        sx={{
          width: spacing(50),
          backgroundColor: palette.background.paper,
          boxShadow:
            '0px 9px 46px 8px rgba(0, 0, 0, 0.12), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 11px 15px -7px rgba(0, 0, 0, 0.20)',
          padding: spacing(3),
          borderRadius: spacing(3),
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(2),
        }}
      >
        <DSTitleTypography>{title}</DSTitleTypography>
        <DSValueTypography sx={{ color: palette.text.secondary }}>
          {message ?? messageByReason[reason]}
        </DSValueTypography>
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spacing(2),
            marginTop: spacing(1),
          }}
        >
          {reason === PreviewUnavailableReason.NEEDS_UPDATE ? (
            <>
              <DSSecondaryButton onClick={onClose} disabled={isUpdating}>
                Cancel
              </DSSecondaryButton>
              <DSPrimaryButton onClick={onUpdate} disabled={isUpdating}>
                {isUpdating ? <DSCircularProgress size={20} /> : 'Update'}
              </DSPrimaryButton>
            </>
          ) : (
            <DSSecondaryButton onClick={onClose}>Close</DSSecondaryButton>
          )}
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

export default PreviewUnavailableModal;
